import React from "react";

import { Link } from "react-router-dom";

import Panel from "ui/Panel";
import PanelTable from "ui/PanelTable";
import Grid, { NumberCell } from "ui/Grid";
import IndexReference from "../IndexReference";

import { formatMs, formatNumber, formatPercent } from "utils/format";
import { useRoutes } from "utils/routes";
import { IndexSelectionResultType, ScanType } from "../util";
import { IndexIcon } from "../Icons";
import { useFeature } from "providers/OrganizationFeatures";
import Tip from "ui/Tip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faFilterSlash } from "@fortawesome/pro-light-svg-icons";

const ScanDetails: React.FunctionComponent<{
  result: IndexSelectionResultType;
  scan: ScanType;
}> = ({ result, scan }) => {
  const { isPrimaryInCluster } = result;
  return (
    <div>
      <ScanInfoPanel
        result={result}
        scan={scan}
        isPrimaryInCluster={isPrimaryInCluster}
      />
      <ScanAffectedQueries
        scan={scan}
        isPrimaryInCluster={isPrimaryInCluster}
      />
      <ConsideredIndexesPanel result={result} scan={scan} />
    </div>
  );
};

export const ParameterizedScanInfo: React.FunctionComponent<{
  parameterizedScanExpected: boolean | null;
}> = ({ parameterizedScanExpected }) => {
  if (parameterizedScanExpected == null) {
    return null;
  }

  return (
    <>
      {parameterizedScanExpected ? (
        <>
          <FontAwesomeIcon icon={faFilter} /> Index use by JOIN clause possible
        </>
      ) : (
        <>
          <FontAwesomeIcon icon={faFilterSlash} /> JOIN clause not expected to
          use index
        </>
      )}{" "}
      <Tip
        content={
          <>
            JOIN conditions can only benefit from an index when looking up
            values from a table on one side of a join in the table on the other
            side of a join. Currently, this only happens with a Nested Loop
            join. This is called a parameterized index scan. pganalyze Index
            Advisor only considers indexes on JOIN conditions if it determines a
            parameterized scan is likely.
          </>
        }
      />
    </>
  );
};

const ScanInfoPanel: React.FunctionComponent<{
  result: IndexSelectionResultType;
  scan: ScanType;
  isPrimaryInCluster: boolean;
}> = ({ result, scan, isPrimaryInCluster }) => {
  const currCost = scan.bestExistingCost;
  const currIdx = result.indexes[currCost.indexId];
  const newCost = scan.bestSelectedCost;
  const newIdx = result.indexes[newCost.indexId];

  const whereClauseStr = scan.whereExpression ? (
    <span className="font-mono">{scan.whereExpression}</span>
  ) : (
    <>&mdash;</>
  );
  const joinClauseStr = scan.joinExpression ? (
    <div>
      <div className="font-mono whitespace-normal mb-1">
        {scan.joinExpression}
      </div>
      <ParameterizedScanInfo
        parameterizedScanExpected={scan.parameterizedScanExpected}
      />
    </div>
  ) : (
    <>&mdash;</>
  );

  return (
    <Panel title="Scan Details">
      <PanelTable horizontal borders>
        <tbody>
          <tr>
            <th>WHERE</th>
            <td colSpan={3}>{whereClauseStr}</td>
          </tr>
          <tr>
            <th>JOIN</th>
            <td colSpan={3}>{joinClauseStr}</td>
          </tr>
          <tr>
            <th>Est. Scans / Min</th>
            <td className="text-right">{formatNumber(scan.scansPerMin, 2)}</td>
            <th></th>
            <td></td>
          </tr>
          {isPrimaryInCluster && (
            <tr>
              <th>Est. Scans / Min in Cluster</th>
              <td className="text-right">
                {formatNumber(scan.scansPerMinCluster, 2)}
              </td>
              <th></th>
              <td></td>
            </tr>
          )}
          <tr>
            <th>Current Est. Cost</th>
            <td className="text-right">{formatNumber(currCost.cost)}</td>
            <th>Current Index</th>
            <td>
              <IndexReference index={currIdx} />
            </td>
          </tr>
          <tr>
            <th>New Est. Cost</th>
            <td className="text-right">
              {newIdx?.id == currIdx?.id ? (
                <>no change</>
              ) : (
                formatNumber(newCost.cost)
              )}
            </td>
            <th>New Index</th>
            <td>
              {newIdx?.id == currIdx?.id ? (
                <>no change</>
              ) : (
                <IndexReference index={newIdx} />
              )}
            </td>
          </tr>
        </tbody>
      </PanelTable>
    </Panel>
  );
};

const ConsideredIndexesPanel: React.FunctionComponent<{
  result: IndexSelectionResultType;
  scan: ScanType;
}> = ({ result, scan }) => {
  const bestCost = Math.min.apply(
    null,
    scan.executionCosts.map((cost) => cost.cost),
  );
  const considered = scan.executionCosts.map((cost) => {
    const index = result.indexes[cost.indexId];
    return {
      ...cost,
      index,
      structure: index?.structure ?? "Seq. Scan",
      indexWriteOverhead: index?.writeOverhead,
      estCost: cost.cost,
      relativeCost: cost.cost / bestCost,
      weightedCost: cost.cost * scan.scansPerMin,
    };
  });
  return (
    <Panel title="Considered Indexes" secondaryTitle={<IndexIconLegend />}>
      <Grid
        defaultSortBy={["estCost", "indexWriteOverhead"]}
        sortParam="sc"
        data={considered}
        columns={[
          {
            field: "structure",
            header: "Definition",
            width: "1fr",
            renderer: function IndexDefinitionCell({ rowData }) {
              return (
                <IndexReference className="max-w-full" index={rowData.index} />
              );
            },
          },
          {
            field: "indexWriteOverhead",
            header: "Index Write Overhead",
            width: "15%",
            style: "number",
            renderer: NumberCell.precision(2),
            nullValue: "n/a",
          },
          {
            field: "estCost",
            header: "Est. Cost",
            width: "10%",
            style: "number",
            renderer: NumberCell,
          },
          {
            field: "relativeCost",
            header: "Relative Cost",
            width: "15%",
            tip: (
              <>
                This represents how the cost of this scan using a given index
                compares to the best possible cost for this scan.
              </>
            ),
            style: "number",
            renderer: function RelativeCostCell({ fieldData }) {
              return `×${formatNumber(fieldData, 2)}`;
            },
          },
          {
            field: "weightedCost",
            header: "Weighted Cost",
            width: "15%",
            tip: (
              <>
                Expected effect of the execution of this scan on your database,
                based on its cost and frequency (<code>Est. Scans/min</code> ×{" "}
                <code>Est. New Cost</code>). Like with cost, the absolute value
                is not meaningful, but you can use relative differences between
                weighted cost to compare different scans.
              </>
            ),
            style: "number",
            renderer: NumberCell.precision(2),
          },
        ]}
      />
    </Panel>
  );
};

const IndexIconLegend: React.FunctionComponent = () => {
  const useConsolidation = useFeature("indexAdvisorV3Consolidation");
  return (
    <div className="flex gap-5 text-sm items-start">
      <span className="flex">
        <IndexIcon
          className="mr-0.5 scale-75 relative -top-[1px]"
          label=""
          selected
        />{" "}
        Create Index
      </span>
      {useConsolidation && (
        <span className="flex">
          <IndexIcon
            className="mr-0.5 scale-75 relative -top-[1px]"
            label=""
            existing
          />{" "}
          Drop Index
        </span>
      )}
      <span className="flex">
        <IndexIcon
          className="mr-0.5 scale-75 relative -top-[1px]"
          label=""
          existing
          selected
        />{" "}
        Existing Index
      </span>
      <span className="flex">
        <IndexIcon
          className="mr-0.5 scale-75 border-2 relative -top-[1px]"
          label=""
        />{" "}
        Considered Index
      </span>
    </div>
  );
};

const ScanAffectedQueries: React.FunctionComponent<{
  scan: ScanType;
  isPrimaryInCluster: boolean;
}> = ({ scan, isPrimaryInCluster }) => {
  const { databaseQuery } = useRoutes();
  return (
    <Panel title="Affected Queries">
      <Grid
        striped
        data={scan.queries}
        sortParam="sq"
        columns={[
          {
            field: "truncatedQuery",
            header: "Query",
            width: "1fr",
            style: "query",
            renderer: function QueryTextCell({ fieldData, rowData }) {
              const url = databaseQuery(rowData.databaseId, rowData.id);
              return (
                <Link title={rowData.normalizedQuery} to={url}>
                  {fieldData}
                </Link>
              );
            },
          },
          {
            field: "serverName",
            header: "Server",
            width: isPrimaryInCluster ? "15%" : false,
          },
          {
            field: "avgTime",
            header: "Avg. Time (ms)",
            width: "10%",
            style: "number",
            renderer: function AvgTimeCell({ fieldData }) {
              return formatMs(fieldData, 2);
            },
          },
          {
            field: "callsPerMinute",
            header: "Calls / Min",
            width: "10%",
            style: "number",
            renderer: function AvgTimeCell({ fieldData }) {
              return formatNumber(fieldData, 3);
            },
          },
          {
            field: "pctOfTotal",
            header: "% All Runtime",
            width: "10%",
            style: "number",
            renderer: function AvgTimeCell({ fieldData }) {
              return formatPercent(fieldData / 100, 2);
            },
          },
        ]}
      />
    </Panel>
  );
};

export default ScanDetails;
